import { useEffect, useState } from 'react';
import {
  Box, Button, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, Typography,
} from '@mui/material';
import { Download } from '@mui/icons-material';
import { previewQuotePdf, downloadQuotePdf } from '../../../services/quoteService';

interface QuotePdfPreviewProps {
  open: boolean;
  onClose: () => void;
  quoteKey: string;
  quoteNumber: string;
}

export const QuotePdfPreview = ({ open, onClose, quoteKey, quoteNumber }: QuotePdfPreviewProps) => {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl);
        setBlobUrl(null);
      }
      return;
    }

    setLoading(true);
    setError(null);
    previewQuotePdf(quoteKey)
      .then((url) => {
        setBlobUrl(url);
        setLoading(false);
      })
      .catch(() => {
        setError('Failed to load PDF preview.');
        setLoading(false);
      });

    return () => {
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, quoteKey]);

  const handleDownload = async () => {
    try {
      await downloadQuotePdf(quoteKey, quoteNumber);
    } catch {
      /* ignore */
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle sx={{ fontWeight: 700 }}>
        PDF Preview &mdash; {quoteNumber}
      </DialogTitle>
      <DialogContent sx={{ p: 0, height: '75vh' }}>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <CircularProgress />
          </Box>
        )}
        {error && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <Typography color="error">{error}</Typography>
          </Box>
        )}
        {blobUrl && !loading && (
          <iframe
            src={blobUrl}
            title="Quote PDF Preview"
            width="100%"
            height="100%"
            style={{ border: 'none' }}
          />
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>
          Close
        </Button>
        <Button
          variant="contained"
          startIcon={<Download />}
          onClick={handleDownload}
          sx={{ textTransform: 'none' }}
        >
          Download
        </Button>
      </DialogActions>
    </Dialog>
  );
};
